import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Text, Content, FlexboxGrid, Row, Col, Form, Grid, Accordion, Stack, Button } from 'rsuite'
import FlowPageLayout from '../../components/layout/FlowPageLayout'
import PortalHeader from '../../components/layout/PortalHeader'
import AnnotatedCheckbox from '../../components/form/AnnotatedCheckbox';
import FormTextField from '../../components/form/FormTextField';
import { checkedValidation, requiredValidation } from '../../common/validation'
import dayjs from 'dayjs'
import usePlaceNavigate from '../../hooks/usePlaceNavigate'
import { areObjectsEqual, constructMarkup, isNullOrUndefined, isNullUndefinedOrEmptyString, objectHash } from '../../common/utils'
import {
    FLOWS_ENUM,
    RENTAL_STRUCTURE_DISPLAY_NAME_DICT,
    RENTAL_STRUCTURE_STATUS_ENUM
} from '../../common/enums'
import IconIndicator from '../../components/user-feedback/IconIndicator'
import {
    selectConfigExpressApplyRentalStructureRequired,
    selectConfigExpressRenewCertifications,
    selectConfigExpressRenewDocuments,
    selectConfigExpressRegistrationTypeVerbiage,
    selectConfigExpressLocalContactVerbiage,
    selectConfigExpressParcelVerbiage,
    selectConfigExpressRenewPropertyDetailsQuestions,
    selectConfigExpressRenewLateFee
} from '../../store/slices/config/configSelectors'
import { C_DEFAULT_RENEW_CERTIFICATION } from '../../common/consts'
import { setCurrentStep, setStepCompletedStatus } from '../../store/slices/flow/flowSlice'
import { selectCurrentStep } from '../../store/slices/flow/flowSelectors'
import Notice from '../../components/user-feedback/Notice'
import { updatePermitData } from '../../store/slices/renew/renewSlice'
import {
    selectDocumentUploadErrors,
    selectRenewalPermitData,
    selectRenewIsPaymentRequired,
    selectRenewLicenseError,
    selectRenewLicenseInProgress
} from '../../store/slices/renew/renewSelectors'
import { renewLicense } from '../../store/slices/renew/renewThunks'
import { setSignatoryName } from '../../store/slices/payment/paymentSlice'
import { payNothingRenewal } from '../../store/slices/payment/paymentThunk'
import { selectCurrentUserEmail } from '../../store/slices/auth/authSelectors'
import LateRenewalNotice from '../../components/renew/LateRenewalNotice'

const ROLES = {
    "PERMIT_HOLDER": 1,
    "OWNER": 2,
    "PROPERTY_MANAGER": 3,
    "OWNER_REPRESENTATIVE": 4
}

const PersonInfo = ({ data, role }) => {
    const [person, setPerson] = useState({});
    const [roleInfo, setRoleInfo] = useState("");
    const selectedLocalContactVerbiage = useSelector(selectConfigExpressLocalContactVerbiage);

    useEffect(() => {
        if (data && role) {
            const personInfo = findInfoForRole(data, role);
            setPerson(personInfo);
        }
    }, [data, role])

    const parseRoleInfoText = (roleInfo) => {
        return [...roleInfo.split("_")].map(word => word[0].toUpperCase() + word.slice(1)).join(" ");
    }

    const findInfoForRole = (data, role) => {
        let _person;

        let hasMultipleOwners = data.registrant.owner.length > 1;
        const people = {
            "owner": data.registrant.owner,
            "property_manager": data.registrant.property_manager ?? data.contacts.propertyManager,
            "permit_holder": data.registrant.registrant,
            "owner_representative": data.contacts.localContact
        }

        if (role === ROLES.PERMIT_HOLDER) {
            let parsedRoleName = parseRoleInfoText(data.registrant.registrantType);
            setRoleInfo(parsedRoleName);
            _person = data.registrant.registrant;
        }
        if (role == ROLES.OWNER) {
            setRoleInfo("Owner");
            _person = data.registrant.owner;
        }
        if (role === ROLES.PROPERTY_MANAGER) {
            setRoleInfo("Property Manager");
            _person = people.property_manager;
        }

        if (role === ROLES.OWNER_REPRESENTATIVE) {
            setRoleInfo({ selectedLocalContactVerbiage });
            _person = people.owner_representative;
        }

        if (hasMultipleOwners && role !== ROLES.OWNER) {
            getIndexOfMatchingPerson(_person, people.owner);
        }

        return {
            details: _person,
            indexOfMatchingOwner: getIndexOfMatchingPerson(_person, people.owner),
            indexOfMatchingPropertyManager: getIndexOfMatchingPerson(_person, people.property_manager),
            hash: objectHash(_person)
        }
    }

    const getIndexOfMatchingPerson = (person, people) => {
        const personArray = Array.isArray(person) ? person : [person];
        const peopleArray = Array.isArray(people) ? people : [people];

        let firstMatchingIndex = -1;
        peopleArray.forEach((people, idx) => {
            personArray.forEach((person, idx) => {
                if (areObjectsEqual(person, people) && firstMatchingIndex === -1) {
                    firstMatchingIndex = idx;
                }
            })
        })
        return firstMatchingIndex;
    }

    const personDisplay = (personDetails, role, number, key) => {
        return (
            <Stack key={key} direction="column" alignItems="flex-start">
                {role?.length > 0 && <h3>{role}{number && ` #${number}`}</h3>}
                <Text className="text-bold">First Name</Text>
                <Text className="mb-4">{personDetails?.firstName}</Text>
                <Text className="text-bold">Last Name</Text>
                <Text className="mb-4">{personDetails?.lastName}</Text>
                <Text className="text-bold">Company Name</Text>
                <Text className="mb-4">{personDetails?.companyName}</Text>
                <Text className="text-bold">Phone Number</Text>
                <Text className="mb-4">{personDetails?.phone}</Text>
                <Text className="text-bold">Email</Text>
                <Text className="mb-4">{personDetails?.email}</Text>
                <Text className="text-bold">Mailing Address</Text>
                <Text className="mb-4">{personDetails?.mailingAddress}</Text>
            </Stack>
        )
    }

    const getSameAsOrUniqueDisplayText = (person) => {
        if (!Array.isArray(person.details)) {
            if (role === ROLES.PERMIT_HOLDER || role === ROLES.OWNER_REPRESENTATIVE) {
                if (person?.indexOfMatchingOwner !== -1 || person?.indexOfMatchingPropertyManager !== -1) {
                    return <Text key={`unique_person_${person.hash}`} className="mb-16">Same as {person?.indexOfMatchingOwner !== -1 ? `owner #${person?.indexOfMatchingOwner + 1}` : `property manager`}</Text>
                }
            }

            if (role === ROLES.PROPERTY_MANAGER) {
                if (person?.indexOfMatchingOwner !== -1) {
                    return <Text key={`unique_person_${person.hash}`} className="mb-16">Same as {`owner #${person?.indexOfMatchingOwner + 1}`}</Text>
                }
            }

            return (
                <>
                    {personDisplay(person.details, roleInfo, "", person.hash)}
                </>
            )
        } else {
            return (
                <Stack direction="column" alignItems="flex-start">

                    {
                        person.details.map((p, i) => {
                            if (role === ROLES.PERMIT_HOLDER || role === ROLES.OWNER_REPRESENTATIVE) {
                                if (person?.indexOfMatchingOwner !== -1 || person?.indexOfMatchingPropertyManager !== -1) {
                                    return <Text key={`person_identifier_${i}`} className="mb-16">Same as {person?.indexOfMatchingOwner !== -1 ? `owner #${person?.indexOfMatchingOwner + 1}` : `property manager #${person?.indexOfMatchingPropertyManager + 1}`}</Text>
                                }
                            }

                            if (role === ROLES.PROPERTY_MANAGER) {
                                if (person?.indexOfMatchingOwner !== -1) {
                                    return <Text key={`person_identifier_${i}`} className="mb-16">Same as {`owner #${person?.indexOfMatchingOwner + 1}`}</Text>
                                }
                            }

                            return (
                                <>
                                    {personDisplay(p, roleInfo, i + 1, `person_identifier_${i}${p.hash}`)}
                                </>
                            )
                        })
                    }
                </Stack>
            )
        }
    }

    return getSameAsOrUniqueDisplayText(person)
}

const RenewConfirmation = () => {
    const placeNavigate = usePlaceNavigate();
    const dispatch = useDispatch();
    const form = useRef();

    const [formValues, setFormValues] = useState({});
    const [formError, setFormError] = useState({});
    const [showMoreState, setShowMoreState] = useState({});
    const [isValidating, setIsValidating] = useState(false);
    const [hasPayNothingError, setHasPayNothingError] = useState(false);
    const [processingPayNothing, setProcessingPayNothing] = useState(false);

    const selectedEmailAddress = useSelector(selectCurrentUserEmail);

    const isPerformingRenew = useSelector(selectRenewLicenseInProgress);
    const renewLicenseError = useSelector(selectRenewLicenseError);
    const uploadDocumentsError = useSelector(selectDocumentUploadErrors);

    const selectedPropertyDetailsQuestions = useSelector(selectConfigExpressRenewPropertyDetailsQuestions);
    const selectedPermitApplicationData = useSelector(selectRenewalPermitData);
    const selectedDocuments = useSelector(selectConfigExpressRenewDocuments);
    const selectedCertifications = useSelector(selectConfigExpressRenewCertifications);
    const selectedCurrentStep = useSelector(selectCurrentStep);
    const selectedRentalStructureRequiredStatus = useSelector(selectConfigExpressApplyRentalStructureRequired);
    const selectedLocalContactVerbiage = useSelector(selectConfigExpressLocalContactVerbiage);
    const selectedPermitTypeVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);
    const selectedParcelVerbiage = useSelector(selectConfigExpressParcelVerbiage);
    const selectedPaymentRequired = useSelector(selectRenewIsPaymentRequired);
    const lateRenewalFee = useSelector(selectConfigExpressRenewLateFee);

    const workInProgress = useMemo(() => {
        return isValidating || isPerformingRenew || processingPayNothing;
    }, [isValidating, isPerformingRenew, processingPayNothing]);

    const displayErrorMessageText = useMemo(() => {
        if (!isNullUndefinedOrEmptyString(renewLicenseError)) {
            return renewLicenseError;
        }
        else if (!isNullOrUndefined(uploadDocumentsError) && uploadDocumentsError.length > 0) {
            return uploadDocumentsError;
        }
        else {
            return "";
        }
    }, [renewLicenseError, uploadDocumentsError])

    const displayCertifications = useMemo(() => {
        if (selectedCertifications && selectedCertifications.length > 0) {
            return selectedCertifications;
        }
        else {
            return [C_DEFAULT_RENEW_CERTIFICATION];
        }
    }, [selectedCertifications]);

    const formModel = useMemo(() => {
        let certificationMap = {}
        if (displayCertifications) {
            displayCertifications?.forEach((c, idx) => certificationMap[`certification_${idx}`] = false)
        }
        return {
            signature: "",
            date: "",
            ...certificationMap
        }
    }, [displayCertifications])

    const validationModel = useMemo(() => {
        let certificationValidationMap = {};
        if (displayCertifications) {
            displayCertifications?.forEach((c, idx) => certificationValidationMap[`certification_${idx}`] = checkedValidation)
        }
        return {
            signature: requiredValidation,
            ...certificationValidationMap
        }
    }, [displayCertifications])

    useEffect(() => {
        setFormValues(formModel)
    }, [formModel, setFormValues])

    const validateCertifications = () => {
        if (!form.current?.check()) return false;
        return true;
    }

    const prepareMandatoryAcknowledgementsObject = () => {
        let mandatoryAcknowledgements = {}
        let count = 1;
        for (const certification of displayCertifications) {
            mandatoryAcknowledgements[`aff_${count}`] = {
                label: certification,
                value: formValues[`certification_${count - 1}`]
            }
            count++;
        }
        return mandatoryAcknowledgements;
    }

    const dispatchConfirmation = () => {
        if (isPerformingRenew) return;
        setIsValidating(true);
        if (!validateCertifications()) {
            setIsValidating(false);
            return;
        }
        setIsValidating(false);
        const mandatoryAcknowledgements = prepareMandatoryAcknowledgementsObject();
        dispatch(
            updatePermitData({
                certifications: {
                    mandatoryAcknowledgements: { ...mandatoryAcknowledgements },
                    signatory: formValues.signature
                }
            })
        );
        dispatch(setSignatoryName(formValues.signatory));
        dispatch(renewLicense(finalizeOnNext));
    }

    const finalizeOnNext = (wasSuccessfull, requiresPayment) => {
        // If successfull go to the success page, else error messages will be displayed
        if (wasSuccessfull) {
            if (requiresPayment) {
                dispatch(setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true }));
                dispatch(setCurrentStep(selectedCurrentStep + 1));
                placeNavigate('/renew-payment');
            }
            else {
                setProcessingPayNothing(true);
                dispatch(payNothingRenewal(selectedEmailAddress, onPayNothingCompleted));
            }
        }
    }

    const onPayNothingCompleted = (successfull) => {
        setHasPayNothingError(successfull);
        setProcessingPayNothing(false);
        if (successfull) {
            placeNavigate('/renew-success')
        }
    }

    const mapSupplementalQuestionAnswerToLabel = (questionKey, answerKey) => {
        let matchingQuestion = selectedPropertyDetailsQuestions.find(q => q.key === questionKey);
        if (matchingQuestion.answer_type !== "radio") return answerKey;
        return constructMarkup(matchingQuestion.radio_options.find(opt => opt.key === answerKey)?.label); // radio types may have html
    }

    return (
        <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={24}>
                <PortalHeader flow={FLOWS_ENUM.RENEW_LICENSE} />
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={24}>
                <Content>
                    <FlowPageLayout
                        title="Confirmation"
                        onNext={dispatchConfirmation}
                    >
                        <Form
                            className="mt-12"
                            ref={form}
                            checkTrigger="blur"
                            formDefaultValue={formModel}
                            onCheck={setFormError}
                            onChange={(f) => {
                                setFormValues({ ...formModel, ...f })
                            }}
                            fluid
                        >
                            <FlexboxGrid
                                className="mt-12 ml-0"
                                xs={24}
                                lg={12}
                            >
                                <FlexboxGrid.Item colspan={24}>
                                    <div role="grid">
                                        <Row>
                                            <Col xs={24}>
                                                {
                                                    selectedPaymentRequired ? (
                                                        <Text>Please confirm the information below to proceed to the payment step.</Text>
                                                    ) : (
                                                        <Text>Please confirm the following information and sign below.</Text>
                                                    )
                                                }
                                            </Col>
                                        </Row>
                                    </div>
                                    {lateRenewalFee && (
                                        <LateRenewalNotice className='mb-24' />
                                    )}
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item
                                    colspan={24}
                                    className="mt-24"
                                >
                                    <div role="grid">
                                        <Row style={{ minHeight: '60px' }}>
                                            <Col xs={24}>
                                                <Grid fluid>
                                                    <Row
                                                        className="mb-8"
                                                    >
                                                        <Col
                                                            xs={24}
                                                            lg={12}
                                                        >
                                                            <Text className="text-bold">Property Address</Text>
                                                            <Text className="mb-8">{selectedPermitApplicationData?.property?.propertyaddress}</Text>

                                                            <Text className="text-bold">{selectedParcelVerbiage}</Text>
                                                            <Text className="mb-8">{selectedPermitApplicationData?.property?.propertyid}</Text>
                                                        </Col>
                                                        <Col
                                                            xs={24}
                                                            lg={12}
                                                        >
                                                            {selectedPermitApplicationData?.property?.unitnumber &&
                                                                <>
                                                                    <Text className="text-bold">Unit Number</Text>
                                                                    <Text className="mb-8">{selectedPermitApplicationData?.property?.unitnumber}</Text>
                                                                </>
                                                            }

                                                            {
                                                                selectedRentalStructureRequiredStatus !== RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && (
                                                                    <>
                                                                        <Text className="text-bold">Rental Structure</Text>
                                                                        <Text className="mb-8">{RENTAL_STRUCTURE_DISPLAY_NAME_DICT[selectedPermitApplicationData?.property?.rentalstructure] || "No option selected"}</Text>
                                                                    </>
                                                                )
                                                            }
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col
                                                            xs={24}
                                                            lg={12}
                                                        >
                                                            <h2 className="mt-8">{selectedPermitTypeVerbiage} Holder</h2>
                                                            <PersonInfo
                                                                className="mb-8"
                                                                data={selectedPermitApplicationData}
                                                                role={ROLES.PERMIT_HOLDER}
                                                            />
                                                            <h2 className="mt-8">Owner{selectedPermitApplicationData?.registrant?.owner?.length > 0 && "s"}</h2>
                                                            <PersonInfo
                                                                data={selectedPermitApplicationData}
                                                                role={ROLES.OWNER}
                                                            />


                                                        </Col>
                                                        <Col
                                                            xs={24}
                                                            lg={12}
                                                        >
                                                            <h2 className="mt-8">Property Manager</h2>
                                                            <PersonInfo
                                                                data={selectedPermitApplicationData}
                                                                role={ROLES.PROPERTY_MANAGER}
                                                            />

                                                            <h2 className="mt-8">{selectedLocalContactVerbiage}</h2>
                                                            <PersonInfo
                                                                data={selectedPermitApplicationData}
                                                                role={ROLES.OWNER_REPRESENTATIVE}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col
                                                            xs={24}
                                                            lg={12}
                                                        >
                                                            {selectedPermitApplicationData?.documents && (
                                                                <>
                                                                    <h2>Verification Documents</h2>
                                                                    {
                                                                        Object.keys(selectedPermitApplicationData?.documents || {})?.length > 0 &&
                                                                            Object.keys(selectedPermitApplicationData?.documents || {}).some(docKey => selectedPermitApplicationData.documents[docKey]?.attachedFiles?.length > 0)
                                                                            ?
                                                                            Object.keys(selectedPermitApplicationData?.documents)?.map((docKey, docGroupIdx) => {
                                                                                const docInfo = selectedDocuments?.find((d) => d.file_key === docKey)
                                                                                const docs = selectedPermitApplicationData?.documents[docKey]?.attachedFiles?.map((file, idx) => <Text key={`document_${file.name}_${idx}`}>{file.name}</Text>)
                                                                                if (!docs || docs.length === 0) return
                                                                                return (
                                                                                    <div key={`document_set_${docGroupIdx}`}>
                                                                                        <Text className="text-bold">{constructMarkup(docInfo?.title)}</Text>
                                                                                        {docs}
                                                                                    </div>
                                                                                )
                                                                            })
                                                                            : <Text>No documents uploaded</Text>
                                                                    }
                                                                </>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                    {
                                                        selectedPermitApplicationData?.propertyDetails?.supplementalQuestions
                                                        && Object.keys(selectedPermitApplicationData?.propertyDetails?.supplementalQuestions)
                                                        && selectedPropertyDetailsQuestions
                                                        && (
                                                            <Row>
                                                                <Col xs={24}>
                                                                    <Accordion
                                                                        className="mh-0 mt-16"
                                                                    >
                                                                        <Accordion.Panel
                                                                            header={<h2 style={{ marginLeft: "-8px", borderBottom: "1px solid #DEDEDE" }}>Supplemental Questions</h2>}
                                                                            className="accordion-without-padding"
                                                                        >
                                                                            {/* Show supplemental questions based on what is is the config for the renew flow*/}
                                                                            {selectedPropertyDetailsQuestions?.map((question, idx) => {
                                                                                // Find matching answer
                                                                                const result = selectedPermitApplicationData?.propertyDetails?.supplementalQuestions?.[question.key];
                                                                                const questionText = result.question;
                                                                                const answer = result.answer;
                                                                                if (typeof answer === "boolean") {
                                                                                    answer = answer === true ? "Yes" : "No";
                                                                                }
                                                                                if (isNullUndefinedOrEmptyString(answer)) {
                                                                                    return;
                                                                                };
                                                                                const mappedAnswer = mapSupplementalQuestionAnswerToLabel(question.key, answer);
                                                                                const allowShowMore = mappedAnswer?.length > 150;
                                                                                const doShowMore = showMoreState[idx] || false;

                                                                                return (

                                                                                    <Grid key={`supplemental_question_${idx}`} className='mb-8' fluid>
                                                                                        <Row>
                                                                                            <Col xs={24} md={10} lg={8}>
                                                                                                <Text>{constructMarkup(questionText)}</Text>
                                                                                            </Col>
                                                                                            <Col xs={24} md={14} lg={16}>
                                                                                                {
                                                                                                    allowShowMore ? (
                                                                                                        <Stack direction="column" alignItems='flex-end'>
                                                                                                            {doShowMore ? <div className='text-bold mb-0'>{mappedAnswer}</div> :
                                                                                                                <div className='truncated-supplemental-question-answer'>
                                                                                                                    <div className='text-bold mb-0'>{mappedAnswer}</div>
                                                                                                                </div>
                                                                                                            }

                                                                                                            <Button
                                                                                                                className="p-0 m-0"
                                                                                                                appearance='link'
                                                                                                                onClick={() => {
                                                                                                                    setShowMoreState(prevState => ({
                                                                                                                        ...prevState,
                                                                                                                        [idx]: !doShowMore
                                                                                                                    }));
                                                                                                                }}
                                                                                                            >
                                                                                                                {doShowMore ? "[Show less]" : "[Show more]"}
                                                                                                            </Button>
                                                                                                        </Stack>
                                                                                                    ) : (
                                                                                                        <div className='text-bold'>{mappedAnswer}</div>
                                                                                                    )
                                                                                                }
                                                                                            </Col>
                                                                                        </Row>
                                                                                    </Grid>








                                                                                    // <Stack key={`supplemental_question_${idx}`} className='mb-8' direction="row" spacing={4}>
                                                                                    //     <Text>{questionText}</Text>
                                                                                    //     <Text className='text-bold'>{mapSupplementalQuestionAnswerToLabel(question.key, answer)}</Text>
                                                                                    // </Stack>
                                                                                )
                                                                            })}
                                                                        </Accordion.Panel>
                                                                    </Accordion>
                                                                </Col>
                                                            </Row>
                                                        )
                                                    }
                                                    <Row>
                                                        <Col xs={24}>
                                                            <h2>Certifications</h2>
                                                            {
                                                                displayCertifications?.map((certificaton, idx) => {
                                                                    return (
                                                                        <AnnotatedCheckbox
                                                                            style={{ marginTop: 0, marginBottom: "16px" }}
                                                                            key={`certification_${idx}`}
                                                                            label={constructMarkup(certificaton)}
                                                                            hasError={formError[`certification_${idx}`]}
                                                                            name={`certification_${idx}`}
                                                                            validationRule={validationModel[`certification_${idx}`]}
                                                                        />
                                                                    )
                                                                })
                                                            }
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col xs={24}>
                                                            <h2>Electronic Signature</h2>
                                                        </Col>
                                                    </Row>
                                                    <Row style={{ display: 'flex', alignItems: 'flex-end' }}>
                                                        <Col xs={12}>
                                                            <FormTextField
                                                                label="Legal Name of Authorized Signatory"
                                                                validationRule={validationModel.signature}
                                                                hasError={formError['signature']}
                                                                name="signature"
                                                                errorMessage="Signature is required"
                                                            />
                                                        </Col>
                                                        <Col xs={12}>
                                                            <FormTextField
                                                                value={dayjs().format("MM/DD/YYYY")}
                                                                label="Date"
                                                                name="date"
                                                                disabled={true}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    {
                                                        workInProgress && (
                                                            <Row className="mv-16">
                                                                <Col xs={24}>
                                                                    <IconIndicator />
                                                                </Col>
                                                            </Row>
                                                        )
                                                    }
                                                    {
                                                        (!workInProgress && !isNullUndefinedOrEmptyString(displayErrorMessageText)) && (
                                                            <Row className="mv-16">
                                                                <Col xs={24}>
                                                                    <Notice message={displayErrorMessageText} />
                                                                </Col>
                                                            </Row>
                                                        )
                                                    }
                                                    {
                                                        !workInProgress && hasPayNothingError && (
                                                            <Row className="mv-16">
                                                                <Col xs={24}>
                                                                    <Notice message='Something went wrong while processing your request.' />
                                                                </Col>
                                                            </Row>
                                                        )
                                                    }
                                                </Grid>
                                            </Col>
                                        </Row>
                                    </div>
                                </FlexboxGrid.Item>
                            </FlexboxGrid>
                        </Form>
                    </FlowPageLayout>
                </Content>
            </FlexboxGrid.Item>
        </FlexboxGrid>
    )
}

export default RenewConfirmation
